import React from "react";
import { Link } from "react-router-dom";
import {
  ChartLineUp,
  Target,
  Table,
  Crosshair,
  DeviceMobile,
  ArrowRight,
  Star,
} from "@phosphor-icons/react";

function StarDivider({ color = "text-white/70" }) {
  return (
    <div className={`flex items-center justify-center gap-1.5 mt-1.5 ${color}`}>
      <span className="h-px w-5 bg-current opacity-50" />
      <Star className="w-2.5 h-2.5" weight="fill" />
      <span className="h-px w-5 bg-current opacity-50" />
    </div>
  );
}

function LetterBadge({ letter, ring, text }) {
  return (
    <div className={`relative flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 rounded-full border-[3px] ${ring}`}>
      <span className={`font-heading font-black text-3xl sm:text-4xl ${text}`}>{letter}</span>
    </div>
  );
}

const CARDS = [
  {
    to: "/common-number",
    title: "Common Number",
    bg: "bg-gradient-to-br from-orange-500 to-red-600",
    text: "text-white",
    content: <LetterBadge letter="C" ring="border-black/70" text="text-black/80" />,
  },
  {
    to: "/blog",
    title: "Blog",
    bg: "bg-white border-2 border-orange-100",
    text: "text-slate-900",
    content: <LetterBadge letter="B" ring="border-orange-500" text="text-orange-600" />,
    divider: "text-orange-400",
  },
  {
    to: "/dream-number",
    title: "Dream Number",
    bg: "bg-gradient-to-br from-orange-500 to-red-600",
    text: "text-white",
    content: <LetterBadge letter="D" ring="border-black/70" text="text-black/80" />,
  },
  {
    to: "/analysis",
    title: "Analysis",
    bg: "bg-gradient-to-br from-teal-500 to-cyan-700",
    text: "text-white",
    content: <ChartLineUp className="w-11 h-11 sm:w-12 sm:h-12 text-white" weight="duotone" />,
  },
  {
    to: "/predict-target",
    title: "Predict Target",
    bg: "bg-gradient-to-br from-stone-600 to-stone-800",
    text: "text-white",
    content: (
      <div className="flex flex-col gap-1 items-center">
        {["Direct", "House", "Ending"].map((l, i) => (
          <div key={l} className="flex items-center gap-1">
            <ArrowRight className="w-3.5 h-3.5 text-amber-400" weight="bold" />
            <span className="text-[9px] sm:text-[11px] font-bold text-white/90">{l}</span>
          </div>
        ))}
      </div>
    ),
  },
  {
    to: "/previous-result",
    title: "Previous Result",
    bg: "bg-gradient-to-br from-teal-500 to-cyan-700",
    text: "text-white",
    content: <Target className="w-11 h-11 sm:w-12 sm:h-12 text-white" weight="duotone" />,
  },
  {
    to: "/calendar",
    title: "Teer Club Chart",
    bg: "bg-gradient-to-br from-emerald-700 to-green-900",
    text: "text-white",
    content: <Table className="w-11 h-11 sm:w-12 sm:h-12 text-emerald-200" weight="duotone" />,
  },
  {
    to: "/contact",
    title: "Counter",
    bg: "bg-white border-2 border-orange-100",
    text: "text-slate-900",
    content: <Crosshair className="w-12 h-12 sm:w-14 sm:h-14 text-orange-600" weight="duotone" />,
    divider: "text-orange-400",
  },
  {
    to: "/about",
    title: "Morning Teer",
    bg: "bg-gradient-to-br from-brand-900 to-slate-900",
    text: "text-white",
    content: <DeviceMobile className="w-11 h-11 sm:w-12 sm:h-12 text-amber-400" weight="duotone" />,
  },
];

export function NavGrid() {
  return (
    <div className="grid grid-cols-3 gap-2.5 sm:gap-4 md:gap-5" data-testid="nav-grid">
      {CARDS.map((c, idx) => (
        <Link
          key={c.to + c.title}
          to={c.to}
          data-testid={`grid-card-${c.title.toLowerCase().replace(/\s+|\//g, "-")}`}
          className={`group relative aspect-square rounded-2xl overflow-hidden shadow-[0_6px_20px_-6px_rgba(0,0,0,0.25)] flex flex-col items-center justify-center text-center p-2 sm:p-3 hover:-translate-y-1 hover:shadow-[0_14px_34px_-10px_rgba(0,0,0,0.35)] transition-transform animate-fade-up ${c.bg}`}
          style={{ animationDelay: `${idx * 45}ms`, opacity: 0 }}
        >
          <div className="flex-1 flex items-center justify-center">{c.content}</div>
          <span className={`font-heading font-extrabold text-[11px] leading-tight sm:text-sm md:text-base ${c.text}`}>
            {c.title}
          </span>
          <StarDivider color={c.divider || (c.text === "text-white" ? "text-white/70" : "text-slate-300")} />
        </Link>
      ))}
    </div>
  );
}
